import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { EMPTY, Observable, Subject, Subscription, catchError, debounceTime, map, switchMap, take } from 'rxjs';
import { Modal } from '@spirit-testing/component-library';
import { ModalEscapeGuardDirective } from '../../components/modal-escape-guard/modal-escape-guard.directive';
import { ActivatedRoute, ParamMap, Params, Router, RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HttpErrorResponse } from '@angular/common/http';
import { ProjectNavComponent } from '../../projects/project-nav/project-nav.component';
import { PageHeaderComponent } from '../../components/page-header/page-header.component';
import { SparklineComponent } from '../../live-dashboard/components/sparkline/sparkline.component';
import { FilterChipComponent } from '../../components/filter-chip/filter-chip.component';
import { SearchBarComponent } from '../../components/search-bar/search-bar.component';
import { CodeIdChipComponent } from '../../components/code-id-chip/code-id-chip.component';
import { SystemBadgeComponent } from '../../components/system-badge/system-badge.component';
import { UmgebungPillComponent } from '../../components/umgebung-pill/umgebung-pill.component';
import { MatrixCellComponent, MatrixStatus } from '../../components/matrix-cell/matrix-cell.component';
import { ResPillComponent, ResPillStatus } from '../../components/res-pill/res-pill.component';
import {
  SegmentedFilterControlComponent,
  SegmentedFilterItem,
} from '../../components/segmented-filter-control/segmented-filter-control.component';
import { TestRunService } from '../../services/test-run.service';
import { SavedViewService } from '../../services/saved-view.service';
import { PageSizePreferenceService } from '../../services/page-size-preference.service';
import { ProjectRoleService } from '../../services/project-role.service';
import { DashboardPinService } from '../../services/dashboard-pin.service';
import { DashboardPin } from '../../models/dashboard-pin.model';
import { ResultStatus } from '../../models/test-run.model';
import {
  TestResultRow,
  TestResultsSummary,
  TestResultsSummaryQuery,
  TrackerBugRef,
} from '../../models/test-results-summary.model';
import { SavedView } from '../../models/saved-view.model';
import { TestresultatImportDialogComponent } from './testresultat-import-dialog/testresultat-import-dialog.component';
import { ResultImportResult } from '../../models/result-import.model';
import { ReportModalComponent } from './report-modal/report-modal.component';
import { ReleaseFreigabeComponent } from './release-freigabe/release-freigabe.component';
import { PlanErgebnisTabelleComponent } from './plan-ergebnis-tabelle/plan-ergebnis-tabelle.component';
import { LaufErgebnisTabelleComponent } from './lauf-ergebnis-tabelle/lauf-ergebnis-tabelle.component';
import { ReleaseService } from '../../services/release.service';
import { Release } from '../../models/release.model';
import { SavedViewOption, SavedViewsMenuComponent } from '../../shared/saved-views/saved-views-menu.component';
import {
  environmentClass,
  matrixIcon,
  parseResultView,
  ReleaseListState,
  RESULT_VIEWS,
  ResultView,
  sourceToSystemKey,
  statusKey,
  systemMeta,
} from './test-results.types';
import {
  buildTestResultsSavedViewFiltersJson,
  parseTestResultsSavedViewFilters,
  releasesAbsteigend,
  TestResultsSavedViewFilters,
  stufeZurUmgebung,
  waehlbareTeststufen,
} from './test-results.utils';
import { formatDurationSeconds } from '../../shared/duration.util';
import { isBranchEnvironment } from '../../shared/branch-name';
import { gleicheUmgebung } from '../../shared/umgebung-name';
import { warnBeforeUnload } from '../../shared/forms/warn-before-unload';

interface FilterChip {
  id: ResultStatus | 'all';
  labelKey: string;
  count: number | null;
}

const RESULT_FILTERS: { id: ResultStatus | 'all'; labelKey: string }[] = [
  { id: 'all', labelKey: 'testResults.filter.all' },
  { id: 'PASSED', labelKey: 'testResults.filter.passed' },
  { id: 'FAILED', labelKey: 'testResults.filter.failed' },
  { id: 'BLOCKED', labelKey: 'testResults.filter.blocked' },
  { id: 'SKIPPED', labelKey: 'testResults.filter.skipped' },
];

/** Wert der Hilfsoption im Teststufe-Select; kann kein Stufenname sein, weil er nur intern vorkommt. */
const UMGEBUNG_OHNE_STUFE_OPTION = '__umgebung-ohne-stufe__';

const SEARCH_DEBOUNCE_MS = 200;
const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
const PAGE_SIZE_ROUTE_KEY = 'test-results';
const MAX_DEFECT_KEYS = 4;
const DEFECT_KEYS_BEFORE_MORE = 3;

@Component({
  selector: 'app-test-results',
  imports: [
    TranslatePipe,
    MatIcon,
    MatProgressSpinner,
    ProjectNavComponent,
    PageHeaderComponent,
    SparklineComponent,
    FilterChipComponent,
    CodeIdChipComponent,
    SystemBadgeComponent,
    UmgebungPillComponent,
    MatrixCellComponent,
    ResPillComponent,
    SegmentedFilterControlComponent,
    SearchBarComponent,
    RouterLink,
    TestresultatImportDialogComponent,
    ReportModalComponent,
    SavedViewsMenuComponent,
    ReleaseFreigabeComponent,
    PlanErgebnisTabelleComponent,
    LaufErgebnisTabelleComponent,
    Modal,
    ModalEscapeGuardDirective,
    MatTooltipModule,
  ],
  templateUrl: './test-results.component.html',
  styleUrl: './test-results.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestResultsComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private testRunService = inject(TestRunService);
  private savedViewService = inject(SavedViewService);
  private releaseService = inject(ReleaseService);
  private pageSizePref = inject(PageSizePreferenceService);
  private translate = inject(TranslateService);
  private snackBar = inject(MatSnackBar);
  private destroyRef = inject(DestroyRef);
  private projectRoleService = inject(ProjectRoleService);
  private dashboardPinService = inject(DashboardPinService);

  protected readonly isReadOnly = computed(
    () =>
      this.projectRoleService.isArchived() ||
      (this.projectRoleService.isProjectReader() && !this.projectRoleService.isTenantAdmin()),
  );

  private readonly snackError: MatSnackBarConfig = { panelClass: 'snackbar-error', duration: 4000 };
  private readonly snackSuccess: MatSnackBarConfig = { panelClass: 'snackbar-success', duration: 4000 };

  /**
   * Alle Refetches laufen ueber diesen Subject in eine `switchMap`. Damit wird ein
   * frueher Request bei einem schnellen zweiten Klick abgebrochen — Antworten koennen
   * sich nicht mehr ueberholen und die Subscription wird bei Component-Destroy
   * automatisch aufgeraeumt.
   */
  private readonly refetch$ = new Subject<TestResultsSummaryQuery>();
  private readonly sucheGeaendert$ = new Subject<void>();

  private readonly currentLang = toSignal(this.translate.onLangChange.pipe(map((e) => e.lang)), {
    initialValue: this.translate.currentLang,
  });

  protected readonly systemMeta = systemMeta;
  protected readonly sourceToSystemKey = sourceToSystemKey;
  protected readonly statusKey = statusKey;
  protected readonly matrixIcon = matrixIcon;
  protected readonly environmentClass = environmentClass;
  protected readonly isBranchEnvironment = isBranchEnvironment;

  protected matrixStatus(status: ResultStatus): MatrixStatus {
    return RESULT_TO_MATRIX_STATUS[status];
  }

  protected resPillStatus(status: ResultStatus): ResPillStatus {
    return RESULT_TO_RES_PILL[status];
  }

  protected readonly pageSizeOptions = PAGE_SIZE_OPTIONS;

  protected readonly projectId: string;
  protected readonly view = signal<ResultView>(parseResultView(this.route.snapshot.queryParamMap.get('sicht')));
  /** Plan-Filter der Sicht „Testläufe" (Query-Parameter `plan`). */
  protected readonly planFilter = signal<string | null>(this.route.snapshot.queryParamMap.get('plan'));

  /** Releases des Projekts; das gewaehlte ist Seitenzustand, den Freigabe-Karte und Sichten teilen. */
  protected readonly releases = signal<Release[]>([]);
  protected readonly selectedReleaseId = signal<string | null>(null);
  protected readonly releasesState = signal<ReleaseListState>('loading');

  private readonly releaseFreigabe = viewChild(ReleaseFreigabeComponent);
  protected readonly showDeactivateConfirm = signal(false);
  private readonly deactivateConfirm$ = new Subject<boolean>();
  private urlRueckfrage?: Subscription;

  protected readonly selectedRelease = computed(
    () => this.releases().find((r) => r.id === this.selectedReleaseId()) ?? null,
  );
  protected readonly stufen = computed(() => waehlbareTeststufen(this.selectedRelease()));
  private readonly stufeName = signal<string | null>(null);
  protected readonly aktiveStufe = computed(() => this.stufen().find((s) => s.name === this.stufeName()) ?? null);
  /** Aus einem alten Link (`umgebung=`, `umg=`) oder einer gespeicherten Ansicht, zu dem das Release keine Stufe hat. */
  protected readonly umgebungOhneStufe = signal<string | null>(null);
  protected readonly umgebungOhneStufeOption = UMGEBUNG_OHNE_STUFE_OPTION;
  protected readonly umgebung = computed(() => this.aktiveStufe()?.umgebung ?? this.umgebungOhneStufe());

  protected readonly suche = signal('');
  protected readonly reportOpen = signal(false);

  protected readonly viewItems = computed<SegmentedFilterItem[]>(() => {
    this.currentLang();
    return RESULT_VIEWS.map((v) => ({
      id: v.id,
      label: this.translate.instant(`testResults.views.${v.id}.label`),
      icon: v.icon,
      testId: `ergebnis-tab-${v.id}`,
    }));
  });

  protected readonly loading = signal(true);
  protected readonly refetching = signal(false);
  protected readonly loadError = signal(false);
  private readonly summary = signal<TestResultsSummary | null>(null);

  protected readonly resultFilter = signal<ResultStatus | 'all'>('all');

  protected readonly pageIndex = signal(0);
  protected readonly pageSize = signal(
    this.pageSizePref.get(PAGE_SIZE_ROUTE_KEY, PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE),
  );

  protected readonly showUploadModal = signal(false);
  /** Zählt die Importe hoch, damit Freigabe-Karte und Sichten ihre Daten neu holen. */
  protected readonly aktualisierung = signal(0);

  private readonly pins = signal<DashboardPin[]>([]);
  private readonly pinning = signal(false);
  /** Ein Pin auf `STAGING` gilt auch für „Staging“ — sonst entstünde eine zweite Kachel mit denselben Zahlen (NEX-2283). */
  protected readonly isPinned = computed(() => {
    const environment = this.umgebung();
    return this.pins().some(
      (p) =>
        p.projectId === this.projectId &&
        p.releaseId === this.selectedReleaseId() &&
        (environment === null ? (p.environment ?? null) === null : gleicheUmgebung(p.environment, environment)),
    );
  });
  protected readonly canPin = computed(() => this.selectedReleaseId() !== null && !this.isPinned() && !this.pinning());

  protected readonly savedViews = signal<SavedView[]>([]);
  protected readonly activeSavedViewName = signal<string | null>(null);
  protected readonly savedViewsError = signal<string | null>(null);

  protected readonly savedViewsOptions = computed<SavedViewOption[]>(() =>
    this.savedViews().map((v) => ({ name: v.name, summary: this.summarizeSavedView(v) })),
  );

  protected readonly rows = computed(() => this.summary()?.rows ?? []);
  protected readonly trend = computed(() => this.summary()?.passRateTrend ?? []);

  protected readonly passRate = computed(() => Math.round(this.summary()?.passRate ?? 0));
  protected readonly executed = computed(() => this.summary()?.executed ?? 0);
  protected readonly passed = computed(() => this.summary()?.passed ?? 0);
  protected readonly failed = computed(() => this.summary()?.failed ?? 0);
  protected readonly openSkipped = computed(() => this.summary()?.openSkipped ?? 0);
  protected readonly blocked = computed(() => this.summary()?.blocked ?? 0);
  protected readonly skipped = computed(() => this.summary()?.skipped ?? 0);

  protected readonly totalElements = computed(() => this.summary()?.totalElements ?? 0);
  protected readonly pageCount = computed(() => Math.max(1, Math.ceil(this.totalElements() / this.pageSize())));
  protected readonly rangeStart = computed(() =>
    this.totalElements() === 0 ? 0 : this.pageIndex() * this.pageSize() + 1,
  );
  protected readonly rangeEnd = computed(() =>
    Math.min(this.totalElements(), (this.pageIndex() + 1) * this.pageSize()),
  );

  protected readonly barWidths = computed(() => {
    const total = this.executed();
    if (total === 0) {
      return { passed: 0, failed: 0, openSkipped: 0 };
    }
    return {
      passed: (this.passed() / total) * 100,
      failed: (this.failed() / total) * 100,
      openSkipped: (this.openSkipped() / total) * 100,
    };
  });

  protected readonly filterChips = computed<FilterChip[]>(() => {
    const counts: Partial<Record<FilterChip['id'], number>> = {
      all: this.executed(),
      PASSED: this.passed(),
      FAILED: this.failed(),
      BLOCKED: this.blocked(),
      SKIPPED: this.skipped(),
    };
    return RESULT_FILTERS.map((f) => ({ ...f, count: counts[f.id] ?? null }));
  });

  constructor() {
    warnBeforeUnload(() => this.hasUnsavedChanges());
    this.projectId = this.route.snapshot.paramMap.get('projectId') ?? '';
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => this.folgeUrl(params));
    // Einzige Subscription — `switchMap` verwirft laufende Requests, wenn ein neuer
    // eintrifft, und `catchError` verhindert, dass ein Fehler die Kette killt.
    this.refetch$
      .pipe(
        switchMap((query) =>
          this.testRunService.getResultsSummary(this.projectId, query).pipe(
            catchError(() => {
              this.handleLoadError();
              return EMPTY;
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((summary) => this.handleLoadSuccess(summary));
    this.sucheGeaendert$.pipe(debounceTime(SEARCH_DEBOUNCE_MS), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.pageIndex.set(0);
      this.reload();
    });
  }

  protected onSucheChange(suche: string): void {
    this.suche.set(suche);
    this.sucheGeaendert$.next();
  }

  /** Vor/Zurück ändert nur die Query, dabei läuft kein canDeactivate; der Entwurf braucht hier eine eigene Rückfrage. */
  private folgeUrl(params: ParamMap): void {
    this.urlRueckfrage?.unsubscribe();
    const sicht = parseResultView(params.get('sicht'));
    const plan = params.get('plan');
    if (sicht === this.view() || !this.hasUnsavedChanges()) {
      this.uebernimmSicht(sicht, plan);
      return;
    }
    this.urlRueckfrage = this.confirmDeactivate()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((discard) => {
        if (discard) this.uebernimmSicht(sicht, plan);
        else this.stelleUrlWiederHer();
      });
  }

  private uebernimmSicht(sicht: ResultView, plan: string | null): void {
    if (sicht !== this.view() || plan !== this.planFilter()) this.clearActiveView();
    this.view.set(sicht);
    this.planFilter.set(plan);
  }

  /** Der Verlaufseintrag kann ein anderes Release oder eine andere Stufe tragen als die Seite zeigt. */
  private stelleUrlWiederHer(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { sicht: this.view(), plan: this.planFilter(), ...this.filterUrlParams() },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /** Ungespeichert kann hier nur der Entwurf der Freigabe-Bewertung sein. */
  hasUnsavedChanges(): boolean {
    return this.releaseFreigabe()?.hasUnsavedChanges() ?? false;
  }

  confirmDeactivate(): Observable<boolean> {
    this.showDeactivateConfirm.set(true);
    return this.deactivateConfirm$.pipe(take(1));
  }

  protected onDeactivateConfirmed(): void {
    this.showDeactivateConfirm.set(false);
    this.deactivateConfirm$.next(true);
  }

  protected onDeactivateCancelled(): void {
    this.showDeactivateConfirm.set(false);
    this.deactivateConfirm$.next(false);
  }

  ngOnInit(): void {
    this.loadSavedViews();
    this.loadReleases();
    this.loadPins();
  }

  private loadPins(): void {
    this.dashboardPinService.list().subscribe({
      next: (pins) => this.pins.set(pins),
      error: () => this.pins.set([]),
    });
  }

  protected pinToDashboard(): void {
    const releaseId = this.selectedReleaseId();
    if (releaseId === null || !this.canPin()) return;
    const environment = this.umgebung();
    this.pinning.set(true);
    this.dashboardPinService
      .create({ projectId: this.projectId, releaseId, ...(environment !== null && { environment }) })
      .subscribe({
        next: (pin) => {
          this.pinning.set(false);
          this.pins.update((list) => [...list, pin]);
          this.snackBar.open(this.translate.instant('testResults.pin.success'), undefined, this.snackSuccess);
        },
        error: (err: unknown) => {
          this.pinning.set(false);
          const code = err instanceof HttpErrorResponse ? err.error?.code : undefined;
          if (code === 'PIN_EXISTS') {
            this.loadPins();
            return;
          }
          const message = code === 'PIN_LIMIT' ? 'testResults.pin.limit' : 'testResults.pin.error';
          this.snackBar.open(this.translate.instant(message), undefined, this.snackError);
        },
      });
  }

  protected loadReleasesAgain(): void {
    this.releasesState.set('loading');
    this.loadReleases();
  }

  /** Vorauswahl: das Release aus dem Query-Parameter `release` (Einstieg aus dem Testplan, NEX-2031), sonst das neueste. */
  private loadReleases(): void {
    const params = this.route.snapshot.queryParamMap;
    const requested = params.get('release');
    this.releaseService.list(this.projectId).subscribe({
      next: (list) => {
        const releases = releasesAbsteigend(list);
        this.releases.set(releases);
        this.selectedReleaseId.set((releases.find((r) => r.id === requested) ?? releases[0])?.id ?? null);
        this.uebernimmFilterAusUrl(params);
        this.releasesState.set('loaded');
        this.reload();
      },
      error: () => {
        this.releases.set([]);
        this.uebernimmFilterAusUrl(params);
        this.releasesState.set('error');
        this.reload();
      },
    });
  }

  /** `stufe` schlägt `umg`, denn die Stufe bringt ihre Umgebung mit; `umgebung` ist der alte Name von `umg`. */
  private uebernimmFilterAusUrl(params: ParamMap): void {
    const stufe = this.stufen().find((s) => s.name === params.get('stufe'));
    if (stufe) {
      this.stufeName.set(stufe.name);
      return;
    }
    this.setzeUmgebung(params.get('umg') ?? params.get('umgebung'));
  }

  private setzeUmgebung(umgebung: string | null): void {
    const stufe = umgebung ? stufeZurUmgebung(this.stufen(), umgebung) : null;
    this.stufeName.set(stufe?.name ?? null);
    this.umgebungOhneStufe.set(stufe ? null : umgebung);
  }

  /** Das Feld zeigt immer das gezeigte Release; erst wenn die Seite den Wechsel übernimmt, folgt es (Rückfrage NEX-1698). */
  protected onReleaseSelected(select: HTMLSelectElement): void {
    const id = select.value;
    select.value = this.selectedReleaseId() ?? '';
    if (id && id !== this.selectedReleaseId()) this.onReleaseChange(id);
  }

  /** Ein Release-Wechsel verwirft den Entwurf der Freigabe-Bewertung, daher dieselbe Rückfrage wie beim Verlassen. */
  private onReleaseChange(releaseId: string): void {
    if (!this.hasUnsavedChanges()) {
      this.applyReleaseChange(releaseId);
      return;
    }
    this.confirmDeactivate()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((discard) => {
        if (discard) this.applyReleaseChange(releaseId);
      });
  }

  /** Ein Plan-Filter gilt nur im Release, aus dem er stammt; eine Stufe nur, wenn es sie dort auch gibt. */
  private applyReleaseChange(releaseId: string): void {
    const stufeVorher = this.aktiveStufe();
    const queryParams: Params = {};
    this.selectedReleaseId.set(releaseId);
    if (this.activeViewTraegtRelease()) this.clearActiveView();
    if (this.planFilter() !== null) {
      this.planFilter.set(null);
      queryParams['plan'] = null;
    }
    if (stufeVorher && !this.aktiveStufe()) {
      this.stufeName.set(null);
      queryParams['stufe'] = null;
      queryParams['umg'] = null;
      queryParams['umgebung'] = null;
      this.clearActiveView();
    }
    if (stufeVorher || this.route.snapshot.queryParamMap.has('release')) queryParams['release'] = releaseId;
    if (Object.keys(queryParams).length > 0) {
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams,
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }
    this.pageIndex.set(0);
    this.reload();
  }

  protected onStufeSelected(wert: string): void {
    if (wert === UMGEBUNG_OHNE_STUFE_OPTION) return;
    this.stufeName.set(wert || null);
    this.umgebungOhneStufe.set(null);
    this.writeFilterToUrl();
    this.pageIndex.set(0);
    this.clearActiveView();
    this.reload();
  }

  protected clearPlanFilter(): void {
    this.planFilter.set(null);
    this.clearActiveView();
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { plan: null },
      queryParamsHandling: 'merge',
    });
  }

  /** Aktueller Filter-/Page-Stand → Server-Refetch. */
  protected reload(): void {
    // Initial-Load zeigt den Full-Page-Spinner; Folge-Refetches lassen den letzten guten
    // Stand stehen, damit ein einzelner 500er oder Netzaussetzer nicht die ganze Seite
    // in einen Fehler-Endzustand zwingt.
    if (this.summary() === null) {
      this.loading.set(true);
    } else {
      this.refetching.set(true);
    }
    this.refetch$.next(this.buildQuery());
  }

  private buildQuery(): TestResultsSummaryQuery {
    const result = this.resultFilter();
    return {
      page: this.pageIndex(),
      size: this.pageSize(),
      environment: this.umgebung() ?? undefined,
      result: result === 'all' ? undefined : result,
      releaseId: this.selectedReleaseId() ?? undefined,
      search: this.suche().trim() || undefined,
    };
  }

  private handleLoadSuccess(summary: TestResultsSummary): void {
    this.summary.set(summary);
    this.loadError.set(false);
    this.loading.set(false);
    this.refetching.set(false);
  }

  private handleLoadError(): void {
    this.loading.set(false);
    this.refetching.set(false);
    if (this.summary() === null) {
      // Kein guter Stand vorhanden → Full-Page-Fehler (Initial-Load).
      this.loadError.set(true);
      return;
    }
    // Guter Stand vorhanden → letzten Stand stehen lassen und Snackbar anzeigen.
    this.snackBar.open(this.translate.instant('testResults.refetchError'), undefined, this.snackError);
  }

  private loadSavedViews(): void {
    this.savedViewService.list(this.projectId, 'TEST_RESULTS').subscribe({
      next: (views) => this.savedViews.set(views),
      error: () => this.savedViews.set([]),
    });
  }

  private summarizeSavedView(view: SavedView): string {
    const filters = parseTestResultsSavedViewFilters(view.filtersJson);
    const parts = [
      filters.sicht ? this.translate.instant(`testResults.views.${filters.sicht}.label`) : null,
      filters.release
        ? this.translate.instant('testResults.savedViews.summaryRelease', { release: filters.release })
        : null,
      filters.planFilter,
      filters.env !== 'all' ? filters.env : null,
      filters.resFilter !== 'all' ? this.translate.instant(`testResults.result.${statusKey(filters.resFilter)}`) : null,
    ].filter((s): s is string => !!s);
    if (parts.length === 0) {
      return this.translate.instant('testResults.savedViews.summaryAll');
    }
    return parts.join(' · ');
  }

  protected onSavedViewSelected(name: string): void {
    const view = this.savedViews().find((v) => v.name === name);
    if (!view) return;
    const filters = parseTestResultsSavedViewFilters(view.filtersJson);
    const release = this.releases().find((r) => r.id === filters.release)?.id ?? null;
    const verwirftEntwurf =
      (filters.sicht !== null && filters.sicht !== this.view()) ||
      (release !== null && release !== this.selectedReleaseId());
    if (!verwirftEntwurf || !this.hasUnsavedChanges()) {
      this.applySavedView(view.name, filters, release);
      return;
    }
    this.confirmDeactivate()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((discard) => {
        if (discard) this.applySavedView(view.name, filters, release);
      });
  }

  /** Alle Filter in einem Zug, danach EIN Refetch. Ein Plan gilt nur in seinem Release; gibt es das nicht mehr, entfällt er. */
  private applySavedView(name: string, filters: TestResultsSavedViewFilters, release: string | null): void {
    const planVorher = this.planFilter();
    const urlParams: Params = {};
    if (release) this.selectedReleaseId.set(release);
    if (filters.sicht) {
      this.view.set(filters.sicht);
      urlParams['sicht'] = filters.sicht;
    }
    this.planFilter.set(filters.release && !release ? null : filters.planFilter);
    if (this.planFilter() !== planVorher) urlParams['plan'] = this.planFilter();
    this.setzeUmgebung(filters.env === 'all' ? null : filters.env);
    this.writeFilterToUrl(urlParams);
    this.resultFilter.set(filters.resFilter);
    this.pageIndex.set(0);
    this.activeSavedViewName.set(name);
    this.reload();
  }

  protected onSavedViewSaved(name: string): void {
    const filtersJson = buildTestResultsSavedViewFiltersJson({
      sicht: this.view(),
      release: this.selectedReleaseId(),
      planFilter: this.planFilter(),
      env: this.umgebung() ?? 'all',
      resFilter: this.resultFilter(),
    });
    this.savedViewService.upsert(this.projectId, { name, context: 'TEST_RESULTS', filtersJson }).subscribe({
      next: (saved) => {
        this.savedViews.update((list) => {
          const rest = list.filter((v) => v.name !== saved.name);
          return [...rest, saved].sort((a, b) => a.name.localeCompare(b.name));
        });
        this.activeSavedViewName.set(saved.name);
        this.savedViewsError.set(null);
      },
      error: () => this.savedViewsError.set(this.translate.instant('testResults.savedViews.saveError')),
    });
  }

  /**
   * Das Loeschen selbst uebernimmt der generische SavedViewsMenuComponent (NEX-786) —
   * hier wird nur noch der lokale State nach einem erfolgreichen Loeschvorgang bereinigt.
   */
  protected onSavedViewDeleted(name: string): void {
    this.savedViews.update((list) => list.filter((v) => v.name !== name));
    if (this.activeSavedViewName() === name) {
      this.activeSavedViewName.set(null);
    }
  }

  protected onSavedViewDeleteFailed(): void {
    this.savedViewsError.set(this.translate.instant('testResults.savedViews.deleteError'));
  }

  /** Eine Ansicht im alten Format kennt kein Release und bleibt deshalb beim Wechsel aktiv. */
  private activeViewTraegtRelease(): boolean {
    const aktiv = this.savedViews().find((v) => v.name === this.activeSavedViewName());
    return !!aktiv && parseTestResultsSavedViewFilters(aktiv.filtersJson).release !== null;
  }

  private clearActiveView(): void {
    if (this.activeSavedViewName() !== null) {
      this.activeSavedViewName.set(null);
    }
  }

  protected openUpload(): void {
    this.showUploadModal.set(true);
  }

  protected onImported(result: ResultImportResult): void {
    const message =
      result.skippedTests > 0
        ? this.translate.instant('testResults.upload.importedWithSkipped', {
            count: result.importedTests,
            skipped: result.skippedTests,
          })
        : this.translate.instant('testResults.upload.imported', { count: result.importedTests });
    this.snackBar.open(message, undefined, this.snackSuccess);
    this.aktualisierung.update((stand) => stand + 1);
    // Reload mit aktuellem Filter/Page-Stand — der `summary()`-Check in reload() haelt den
    // Full-Page-Spinner ohnehin unten, sobald bereits einmal Daten geladen wurden.
    this.reload();
  }

  protected setView(id: string | null): void {
    const next = parseResultView(id);
    if (next === this.view()) return;
    if (!this.hasUnsavedChanges()) {
      this.applyView(next);
      return;
    }
    this.confirmDeactivate()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((discard) => {
        if (discard) this.applyView(next);
      });
  }

  private applyView(next: ResultView): void {
    const plan = next === 'laeufe' ? this.planFilter() : null;
    this.clearActiveView();
    this.view.set(next);
    this.planFilter.set(plan);
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { sicht: next, plan },
      queryParamsHandling: 'merge',
    });
  }

  /** „Testläufe des Plans" aus der Sicht „Testpläne": Sicht „Testläufe" mit Plan-Filter. */
  protected openPlanLaeufe(planKey: string | null): void {
    this.clearActiveView();
    this.view.set('laeufe');
    this.planFilter.set(planKey);
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { sicht: 'laeufe', plan: planKey },
      queryParamsHandling: 'merge',
    });
  }

  /** replaceUrl: ein Filterwechsel soll keinen eigenen Eintrag im Browserverlauf erzeugen. */
  private writeFilterToUrl(weitere: Params = {}): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { ...weitere, ...this.filterUrlParams() },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private filterUrlParams(): Params {
    return {
      stufe: this.aktiveStufe()?.name ?? null,
      umg: this.umgebungOhneStufe(),
      umgebung: null,
      release: this.selectedReleaseId(),
    };
  }

  protected setResult(result: ResultStatus | 'all'): void {
    if (this.resultFilter() === result) return;
    this.resultFilter.set(result);
    this.pageIndex.set(0);
    this.clearActiveView();
    this.reload();
  }

  protected onPageChange(index: number): void {
    if (index < 0 || index >= this.pageCount() || index === this.pageIndex()) {
      return;
    }
    this.pageIndex.set(index);
    this.reload();
  }

  protected onPageSizeChange(size: number): void {
    if (!size || size === this.pageSize()) {
      return;
    }
    this.pageSize.set(size);
    this.pageIndex.set(0);
    this.pageSizePref.set(PAGE_SIZE_ROUTE_KEY, size, PAGE_SIZE_OPTIONS);
    this.reload();
  }

  protected isDeferred(row: TestResultRow): boolean {
    return systemMeta(sourceToSystemKey(row.source)).deferred;
  }

  protected formatDuration(seconds: number | null): string {
    return formatDurationSeconds(seconds);
  }

  /**
   * RouterLink target of the row: the test case's run history (NEX-731), the drilldown
   * from the overview. Null for deferred sources, which are shown but not navigable.
   */
  protected rowLink(row: TestResultRow): unknown[] | null {
    if (this.isDeferred(row)) {
      return null;
    }
    return ['/projects', this.projectId, 'test-results', 'history', row.testCaseId];
  }

  protected visibleDefects(row: TestResultRow): TrackerBugRef[] {
    const defects = this.defectEntries(row);
    return defects.length > MAX_DEFECT_KEYS ? defects.slice(0, DEFECT_KEYS_BEFORE_MORE) : defects;
  }

  protected hiddenDefects(row: TestResultRow): TrackerBugRef[] {
    return this.defectEntries(row).slice(this.visibleDefects(row).length);
  }

  protected defectKeys(defects: TrackerBugRef[]): string {
    return defects.map((d) => d.key).join(', ');
  }

  private defectEntries(row: TestResultRow): TrackerBugRef[] {
    const defects = row.defects ?? [];
    const trackerBugs = row.trackerBugs ?? [];
    const trackerKeys = new Set(trackerBugs.map((t) => t.key));
    return [
      ...trackerBugs.map((t) => ({
        key: t.key,
        blocking: t.blocking || defects.some((d) => d.key === t.key && d.blocking),
      })),
      ...defects.filter((d) => !trackerKeys.has(d.key)).map((d) => ({ key: d.key, blocking: d.blocking })),
    ];
  }

  protected defectsDescriptionId(row: TestResultRow): string {
    return `tr-defects-${row.testCaseId}`;
  }

  /** Die Zeile trägt ein eigenes aria-label; die Defects kommen daher als Beschreibung dazu. */
  protected defectsDescription(row: TestResultRow): string {
    const defects = this.defectEntries(row);
    const blocking = this.translate.instant('testResults.defects.blocking');
    const items = defects.length
      ? defects.map((d) => (d.blocking ? `${d.key} (${blocking})` : d.key)).join(', ')
      : this.translate.instant('testResults.defects.none');
    return `${this.translate.instant('testResults.table.defects')}: ${items}`;
  }

  /** Concise aggregated label so screen readers announce the row instead of every cell. */
  protected rowAriaLabel(row: TestResultRow): string {
    return this.translate.instant('testResults.rowAriaLabel', {
      key: row.testCaseKey ?? '',
      title: row.title ?? '',
      result: this.translate.instant('testResults.result.' + statusKey(row.lastResult)),
    });
  }
}

const RESULT_TO_MATRIX_STATUS: Record<ResultStatus, MatrixStatus> = {
  PASSED: 'passed',
  FAILED: 'failed',
  BLOCKED: 'blocked',
  RUNNING: 'running',
  SKIPPED: 'skipped',
  NOT_RUN: 'not-run',
};

const RESULT_TO_RES_PILL: Record<ResultStatus, ResPillStatus> = {
  PASSED: 'passed',
  FAILED: 'failed',
  BLOCKED: 'blocked',
  RUNNING: 'running',
  SKIPPED: 'skipped',
  NOT_RUN: 'not-run',
};
